import { css, html, nothing } from "lit";
import { ChromeElement, defineChromeElement } from "../chrome/ChromeElement";
import { fallbackLabels, type LabelTable, type Labels } from "../chrome/chromeLabels";
import { chromeControlStyles } from "../chrome/chromeStyles";
import { fitTexts } from "../chrome/fitText";
import type { IntroSplashCardModel, IntroSplashPort } from "./IntroSplashPort";
import { fitScale } from "@slot/presentation";
import type { FitLimits, ScreenShape } from "@slot/presentation";

export const INTRO_SPLASH_LABELS = {
  volatility: ["intro.volatility", "Volatility"],
  prompt: ["intro.prompt", "Tap to start"],
} as const satisfies LabelTable;
export type IntroSplashLabels = Labels<typeof INTRO_SPLASH_LABELS>;

export const INTRO_SPLASH_TAG = "slot-intro-splash";

/**
 * The splash art as image URLs — the boot bundle's background and logo, the shared bundle's card; null leaves the slot
 * empty. `backgroundPortrait`, when given, shows instead of `background` on a viewport at least as tall as wide (CSS's
 * orientation, as the boot loader picks it: decision 69).
 */
export interface IntroSplashArt {
  background: string | null;
  backgroundPortrait?: string | null;
  logo: string | null;
  card: string | null;
}

/** `IntroSplashPanel.prefab` geometry (design units, y-down), as the Pixi view carried it. */
const SPLASH = {
  logo: { width: 456.1, height: 311.3, top: 117.7 },
  volatility: { top: 455.4, width: 205.5, cell: 40, pip: 32, count: 5 },
  cards: { top: 615.6, bottom: 395.4, rowPitch: 750, row: { width: 2200, height: 900 }, portraitY: 400 },
  /** The portrait carousel: one card at a time, a slide + fade to the next after the dwell, looping. */
  carousel: { dwellSeconds: 2, slideDistanceX: 600, transitionSeconds: 0.4 },
} as const;
const ROW_LIMITS: Record<ScreenShape, FitLimits> = { desktop: { maxWidth: 2200 }, mobile: { maxWidth: 750 }, mobile_landscape: { maxWidth: 2200 } };

/**
 * `<slot-intro-splash>`: the branded intro over everything at boot — blackout, the cover background,
 * the logo, the volatility strip, the age and version badges, the cards and the "Tap to start"
 * prompt. Desktop and landscape lay the cards out in a fitted row; portrait cycles them one at a
 * time with a CSS animation (a dev pause freezes it with the rest of the chrome). Each card stacks
 * its icon, title and body (decision 18). The first pointer-up or click anywhere is the gesture the
 * boot waits for. With its content off (decision 77) it is a title intro's chrome: transparent, only
 * its badges and its prompt, pulsing, over the canvas where the title draws its intro.
 */
export class IntroSplashElement extends ChromeElement implements IntroSplashPort {
  public static override properties = {
    ...ChromeElement.properties,
    open: { type: Boolean, reflect: true },
    labels: { attribute: false },
    art: { attribute: false },
    cards: { state: true },
    volatility: { state: true },
    headlineShown: { state: true },
    ageYears: { state: true },
    version: { state: true },
    contentShown: { state: true },
  };

  public declare open: boolean;
  public declare labels: IntroSplashLabels;
  public declare art: IntroSplashArt;
  private declare cards: readonly IntroSplashCardModel[];
  private declare volatility: number;
  private declare headlineShown: boolean;
  private declare ageYears: number | null;
  private declare version: string;
  private declare contentShown: boolean;
  private resolveGesture: (() => void) | null = null;

  public constructor() {
    super();
    this.open = false;
    this.labels = fallbackLabels(INTRO_SPLASH_LABELS);
    this.art = { background: null, logo: null, card: null };
    this.cards = [];
    this.volatility = 0;
    this.headlineShown = false;
    this.ageYears = null;
    this.version = "";
    this.contentShown = true;
    this.addEventListener("pointerup", this.onGesture);
    this.addEventListener("click", this.onGesture);
  }

  public show(): void {
    this.open = true;
  }

  public hide(): void {
    this.open = false;
  }

  public waitForFirstGesture(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.resolveGesture = resolve;
    });
  }

  public setCards(cards: readonly IntroSplashCardModel[]): void {
    this.cards = cards;
  }

  public setVolatility(rating: number): void {
    this.volatility = Number.isFinite(rating) ? Math.max(0, Math.min(SPLASH.volatility.count, Math.floor(rating))) : 0;
  }

  public setHeadlineBadgeShown(shown: boolean): void {
    this.headlineShown = shown;
  }

  public setAgeBadge(years: number | null): void {
    this.ageYears = years;
  }

  public setVersionText(text: string): void {
    this.version = text;
  }

  public setContentShown(shown: boolean): void {
    this.contentShown = shown;
    this.toggleAttribute("data-title-intro", !shown);
  }

  private readonly onGesture = (): void => {
    const resolve = this.resolveGesture;
    this.resolveGesture = null;
    resolve?.();
  };

  public static override styles = [
    chromeControlStyles,
    css`
      :host { display: none; position: absolute; inset: 0; overflow: hidden; pointer-events: auto; color: #fff; font-weight: 500; }
      :host([open]) { display: block; }
      [hidden] { display: none !important; }
      .blackout { position: absolute; inset: 0; background: #000; }
      /* \`ResponsiveCoverBackground\` at 1.01 overscan. */
      .background { position: absolute; inset: -0.5%; background-size: cover; background-position: center; background-image: var(--intro-background, none); }
      @media (orientation: portrait) { .background { background-image: var(--intro-background-portrait, var(--intro-background, none)); } }
      .logo { position: absolute; left: 50%; top: 117.7px; width: 456.1px; height: 311.3px; transform: translateX(-50%); background-size: contain; background-repeat: no-repeat; background-position: center; }
      /* \`AgeBadge\` 300×100 at right 20 / top 20; its 118.7×50.4 label box sits top-right. */
      .age { position: absolute; right: 20px; top: 20px; width: 300px; height: 100px; display: flex; justify-content: flex-end; align-items: flex-start; }
      .age .box { width: 118.7px; height: 50.4px; display: flex; align-items: center; justify-content: flex-end; font-size: 40px; }
      .version { position: absolute; left: 20px; bottom: 20px; width: 118.7px; height: 50.4px; display: flex; align-items: center; font-size: 40px; }
      .volatility-label { position: absolute; left: 50%; top: 428.6px; width: 332.8px; height: 50.4px; transform: translateX(-50%); display: flex; align-items: center; justify-content: center; font-size: 40px; text-shadow: 0 0 10px #000; }
      /* The pip strip: 40-unit cells from the strip's left edge (not centred — kept from the prefab), 32-unit pips. */
      .pips { position: absolute; left: 50%; top: 455.4px; width: 205.5px; height: 100px; transform: translateX(-50%); }
      .pip { position: absolute; top: 34px; width: 32px; height: 32px; border-radius: 6px; background: #a3a3a3; }
      .pip[data-active] { background: #80ee4c; }
      .headline { position: absolute; right: 101.5px; top: 871.6px; width: 200.7px; height: 191.1px; }
      .cards { position: absolute; left: 0; right: 0; top: 615.6px; bottom: 395.4px; }
      .card { position: absolute; left: 50%; top: 50%; width: 600px; height: 909px; transform: translate(-50%, -50%) translateX(var(--card-x, 0px)) scale(var(--card-scale, 1)); transform-origin: center; }
      :host([data-shape="mobile"]) .card { top: 400px; transform: translate(-50%, -50%); animation: intro-card var(--carousel-period) linear infinite; animation-delay: var(--card-delay); animation-fill-mode: backwards; }
      :host([data-shape="mobile"]) .card[data-single] { animation: none; }
      .card-bg { position: absolute; inset: 0; background-size: 100% 100%; }
      .icon { position: absolute; left: 50%; top: 60px; width: 478.9px; height: 431px; transform: translateX(-50%); background-size: contain; background-repeat: no-repeat; background-position: center; }
      .title { position: absolute; left: 50%; top: 521px; width: 556px; height: 74.3px; transform: translateX(-50%); display: flex; align-items: center; justify-content: center; font-size: 39.6px; font-weight: 700; color: #553f12; }
      .body { position: absolute; left: 50%; top: 615px; width: 472.5px; height: 162.4px; transform: translateX(-50%); font-size: 39.6px; line-height: 50.3px; color: #553f12; text-align: center; }
      /* The body wraps in its box and, like the Pixi fitted text, shrinks from the top when it runs long. */
      .body [data-fit] { white-space: normal; width: 472.5px; transform-origin: top center; }
      .prompt { position: absolute; left: 50%; bottom: 100px; width: 1097px; height: 120.2px; transform: translateX(-50%); display: flex; align-items: center; justify-content: center; font-size: 71.2px; }
      /* A title's intro under it: the prompt pulses over the canvas, legible on any art. */
      :host([data-title-intro]) .prompt { text-shadow: 0 0 12px #000, 0 2px 4px #000; animation: intro-prompt 1.6s ease-in-out infinite; }
      @keyframes intro-prompt { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
      @media (prefers-reduced-motion: reduce) { :host([data-title-intro]) .prompt { animation: none; } }
    `,
  ];

  protected override updated(): void {
    fitTexts(this.renderRoot);
  }

  /** The row's scale and per-card offsets (desktop / landscape), from the prefab row fitted into the container. */
  private rowLayout(): { scale: number; x: (index: number) => number } {
    const { width, height } = this.view.design;
    const cardsHeight = Math.max(0, height - SPLASH.cards.top - SPLASH.cards.bottom);
    const scale = fitScale(SPLASH.cards.row, ROW_LIMITS[this.view.shape], { width, height: cardsHeight });
    const count = this.cards.length;
    return { scale, x: (index) => (index - (count - 1) / 2) * SPLASH.cards.rowPitch * scale };
  }

  /** The portrait carousel as keyframes: enter from the right, dwell, exit left, stay hidden until the loop wraps. */
  private carouselStyle(): string {
    const { dwellSeconds: dwell, transitionSeconds: t, slideDistanceX: slide } = SPLASH.carousel;
    const period = this.cards.length * (dwell + t);
    const pct = (seconds: number) => ((seconds / period) * 100).toFixed(3);
    return `@keyframes intro-card { 0% { opacity: 0; transform: translate(-50%, -50%) translateX(${slide}px); } ${pct(t)}% { opacity: 1; transform: translate(-50%, -50%); } ${pct(t + dwell)}% { opacity: 1; transform: translate(-50%, -50%); } ${pct(2 * t + dwell)}% { opacity: 0; transform: translate(-50%, -50%) translateX(${-slide}px); } 100% { opacity: 0; transform: translate(-50%, -50%) translateX(${-slide}px); } }`;
  }

  /** The background's two images as the custom properties the media query picks from. */
  private backgroundStyle(): string {
    const property = (name: string, value: string | null | undefined): string => (value ? `${name}: url("${value}");` : "");
    return `${property("--intro-background", this.art.background)} ${property("--intro-background-portrait", this.art.backgroundPortrait)}`.trim();
  }

  protected override render() {
    const url = (value: string | null) => (value ? `background-image: url("${value}")` : "");
    const row = this.rowLayout();
    const { dwellSeconds, transitionSeconds } = SPLASH.carousel;
    const period = this.cards.length * (dwellSeconds + transitionSeconds);
    const pips = Array.from({ length: SPLASH.volatility.count }, (_, i) => i);
    const chrome = html`
      <div class="age" data-part="age-badge" ?hidden=${this.ageYears === null}><div class="box"><span data-fit data-part="age">${this.ageYears === null ? "" : `${this.ageYears}+`}</span></div></div>
      <div class="version" data-part="version"><span data-fit>${this.version}</span></div>
      <div class="prompt" data-part="prompt"><span data-fit>${this.labels.prompt}</span></div>
    `;
    // A title's intro draws the rest in the canvas under it (decision 77).
    if (!this.contentShown) return chrome;
    return html`
      <style>${this.cards.length > 1 ? this.carouselStyle() : ""}</style>
      <div class="blackout"></div>
      <div class="background" style=${this.backgroundStyle()}></div>
      <div class="logo" data-part="logo" style=${url(this.art.logo)}></div>
      <div class="volatility-label"><span data-fit data-part="volatility-label">${this.labels.volatility}</span></div>
      <div class="pips" data-part="volatility">
        ${pips.map((i) => html`<div class="pip" data-part="pip" data-index=${i + 1} ?data-active=${i < this.volatility} style="left: ${(SPLASH.volatility.cell - SPLASH.volatility.pip) / 2 + i * SPLASH.volatility.cell}px"></div>`)}
      </div>
      <div class="headline" data-part="headline-badge" ?hidden=${!this.headlineShown}></div>
      <div class="cards" data-part="cards">
        ${this.cards.map(
          (card, index) => html`<div class="card" data-part="card" data-index=${index + 1} ?data-single=${this.cards.length <= 1} style="--card-x: ${row.x(index)}px; --card-scale: ${row.scale}; --carousel-period: ${period}s; --card-delay: ${index * (dwellSeconds + transitionSeconds) - transitionSeconds}s">
            <div class="card-bg" style=${url(this.art.card)}></div>
            ${card.iconUrl ? html`<div class="icon" data-part="card-icon" style=${url(card.iconUrl)}></div>` : nothing}
            <div class="title" data-part="card-title"><span data-fit>${card.title}</span></div>
            <div class="body" data-part="card-body"><span data-fit>${card.body}</span></div>
          </div>`,
        )}
      </div>
      ${chrome}
    `;
  }
}

export function defineIntroSplashElement(): void {
  defineChromeElement(INTRO_SPLASH_TAG, IntroSplashElement);
}
